import type { OpenAPI } from "../../interfaces"
import SearchParams from "../searchParams"

export const challenges = (api) => ({
  getAll(
    params: {
      query?: string
    },
    success: (data: OpenAPI.GET<"/challenges/all">["response"]) => void,
    fail,
  ) {
    const queryParams = new SearchParams(params)
    api.authenticatedRequest("GET", `/challenges/all${queryParams.toSafeString()}`, null, success, fail)
  },
  create(name, success, fail) {
    api.authenticatedRequest("POST", "/challenges", { name }, success, fail)
  },
  createWithData(data: Partial<OpenAPI.POST<"/challenges">["requestBody"]>, success, fail) {
    api.authenticatedRequest("POST", "/challenges", data, success, fail)
  },
  createGatedChallenge(data, id, success, fail) {
    api.authenticatedRequest("POST", `/challenges/${id}/gatedChallenge/create`, data, success, fail)
  },
  getAudienceSize(visibility, success, fail) {
    api.authenticatedRequest("POST", "/challenges/new/audience", { visibility }, success, fail)
  },
  get(id: string, success: (data: OpenAPI.GET<"/challenges/{id}">["response"]) => void, fail) {
    api.maybeAuthenticatedRequest("GET", `/challenges/${id}`, null, success, fail)
  },
  getOverview(id, params, success, fail) {
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/overview?${params.filter ? `filter=${params.filter}` : ""}`,
      null,
      success,
      fail,
    )
  },
  getProblems(id, success, fail) {
    api.authenticatedRequest("GET", `/challenges/${id}/problems`, null, success, fail)
  },
  getLabels(success: (data: OpenAPI.GET<"/challenges/labels">["response"]) => void, fail) {
    api.authenticatedRequest("GET", `/challenges/labels`, null, success, fail)
  },
  update(
    id: string,
    data: OpenAPI.PUT<"/challenges/{id}">["requestBody"],
    success: (data: OpenAPI.PUT<"/challenges/{id}">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("PUT", `/challenges/${id}`, data, success, fail)
  },
  delete(id, success, fail) {
    api.authenticatedRequest("DELETE", `/challenges/${id}`, null, success, fail)
  },
  setStatus(id, data, success, fail) {
    api.authenticatedRequest("PUT", `/challenges/${id}/status`, data, success, fail)
  },
  updateStage(id, stage, openWithComms, success, fail) {
    if (!stage) api.authenticatedRequest("DELETE", `/challenges/${id}/stage`, null, success, fail)
    else
      api.authenticatedRequest(
        "PUT",
        `/challenges/${id}/stage/${stage}?openWithComms=${openWithComms}`,
        null,
        success,
        fail,
      )
  },
  getIdeas(
    id: string,
    params: OpenAPI.GET<"/challenges/{id}/ideas">["parameters"]["query"],
    success: (data: OpenAPI.GET<"/challenges/{id}/ideas">["response"]) => void,
    fail,
  ) {
    const queryParams = new SearchParams(params)
    api.authenticatedRequest("GET", `/challenges/${id}/ideas${queryParams.toSafeString()}`, null, success, fail)
  },
  getIdeaFilters(id: string, success: (data: OpenAPI.GET<"/challenges/{id}/ideas/filters">["response"]) => void, fail) {
    api.authenticatedRequest("GET", `/challenges/${id}/ideas/filters`, null, success, fail)
  },
  getIdeaAssignees(id, success, fail) {
    api.authenticatedRequest("GET", `/challenges/${id}/ideas/assignees`, null, success, fail)
  },
  updateIdeasStatus(id, data, success, fail) {
    api.authenticatedRequest("PUT", `/challenges/${id}/ideas/status`, data, success, fail)
  },
  addIdeasLabels(
    id,
    data: OpenAPI.PUT<"/challenges/{id}/ideas/status/labels">["requestBody"],
    success: (data: OpenAPI.PUT<"/challenges/{id}/ideas/status/labels">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("PUT", `/challenges/${id}/ideas/status/labels`, data, success, fail)
  },
  removeIdeasLabels(
    id,
    data: OpenAPI.PUT<"/challenges/{id}/ideas/status/labels">["requestBody"],
    success: (data: OpenAPI.PUT<"/challenges/{id}/ideas/status/labels">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("DELETE", `/challenges/${id}/ideas/status/labels`, data, success, fail)
  },
  getTemplate(id: string, success: (data: OpenAPI.GET<"/challenges/{id}/template">["response"]) => void, fail) {
    api.maybeAuthenticatedRequest("GET", `/challenges/${id}/template`, null, success, fail)
  },
  updateTemplate(id, data, success, fail) {
    api.authenticatedRequest("PUT", `/challenges/${id}/template`, data, success, fail)
  },
  getPostable(challenge, success, fail) {
    api.authenticatedRequest("GET", `/organisations/postable/new?challenge=${challenge || ""}`, null, success, fail)
  },
  createIdea(id, name, success, fail) {
    api.maybeAuthenticatedRequest("POST", `/challenges/${id}/ideas`, { name }, success, fail)
  },
  createAnnouncement(id, data, success, fail) {
    api.authenticatedRequest("POST", `/challenges/${id}/announcements`, data, success, fail)
  },
  getAnnouncements(id, success, fail) {
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/announcements`,
      null,
      (data) => success?.(data.announcements),
      fail,
    )
  },
  deleteAnnouncement(id, announcementId, success, fail) {
    api.authenticatedRequest("DELETE", `/challenges/${id}/announcements/${announcementId}`, null, success, fail)
  },
  updateAnnouncement(id, announcementId, data, success, fail) {
    api.authenticatedRequest("PUT", `/challenges/${id}/announcements/${announcementId}`, data, success, fail)
  },
  getReports(id, success, fail) {
    api.authenticatedRequest("GET", `/challenges/${id}/reports`, null, success, fail)
  },
  getChartData(id, data, success, fail) {
    api.authenticatedRequest("GET", `/challenges/${id}/charts?filter=${data.filter}`, null, success, fail)
  },
  getOptions(id, success, fail) {
    api.authenticatedRequest("GET", `/challenges/${id}/charts/options`, null, success, fail)
  },
  assignAssessors(id, ideas, users, assessmentRoundId, success, fail) {
    api.authenticatedRequest("PUT", `/challenges/${id}/assessors`, { ideas, users, assessmentRoundId }, success, fail)
  },
  assignAssessorAssignments(
    id,
    assignments: OpenAPI.PUT<"/challenges/{id}/assessors/assignment">["requestBody"],
    success: (data: OpenAPI.PUT<"/challenges/{id}/assessors/assignment">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("PUT", `/challenges/${id}/assessors/assignment`, assignments, success, fail)
  },
  remindAssessor(challengeId, assessorId, assessmentRoundId, success, fail) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${challengeId}/assessors/${assessorId}/remind`,
      { assessmentRoundId },
      success,
      fail,
    )
  },
  unassignAssessors(id, ideas, users, assessmentRoundId, success, fail) {
    api.authenticatedRequest(
      "DELETE",
      `/challenges/${id}/assessors`,
      { ideas, users, assessmentRoundId },
      success,
      fail,
    )
  },
  getAssignedAssessments(
    id: string,
    success: (data: OpenAPI.GET<"/challenges/{id}/assessments">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("GET", `/challenges/${id}/assessments`, null, success, fail)
  },
  submitAllAssessments(id, assessmentRoundId, success, fail) {
    api.authenticatedRequest("POST", `/challenges/${id}/assessments/${assessmentRoundId}`, null, success, fail)
  },
  connectService(id, service, success, fail) {
    api.authenticatedRequest("POST", `/challenges/${id}/connections/${service}`, null, success, fail)
  },
  disconnectService(id, service, success, fail) {
    api.authenticatedRequest("DELETE", `/challenges/${id}/connections/${service}`, null, success, fail)
  },
  updateServiceWithData(id, service, data, success, fail) {
    api.authenticatedRequest("POST", `/challenges/${id}/connections/${service}`, data, success, fail)
  },
  follow(id, success, fail) {
    api.authenticatedRequest("POST", `/challenges/${id}/followers`, null, success, fail)
  },
  unfollow(id, success, fail) {
    api.authenticatedRequest("DELETE", `/challenges/${id}/followers`, null, success, fail)
  },
  followers(id, params, success, fail) {
    const queryParams = new SearchParams(params).toString()
    api.authenticatedRequest("GET", `/challenges/${id}/followers?${queryParams}`, null, success, fail)
  },
  curate(id, toCurate, success, fail) {
    api.authenticatedRequest(toCurate ? "PUT" : "DELETE", `/challenges/${id}/curate`, null, success, fail)
  },
  getIpSignatureFields(success, fail) {
    api.authenticatedRequest("GET", "/challenges/ipmanagement/fields", null, success, fail)
  },
  getLatestIpStatement(id, success, fail) {
    api.authenticatedRequest("GET", `/challenges/${id}/ipstatements/statements`, null, success, fail)
  },
  agreeToLatestIpStatement(id, statementId, data, success, fail) {
    api.authenticatedRequest(
      "PUT",
      `/challenges/${id}/ipstatements/statements/${statementId}/agreement`,
      data,
      success,
      fail,
    )
  },
  getAgreementsForIPStatement(id, statementId, success, fail) {
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/ipstatements/statements/${statementId}/agreements`,
      null,
      success,
      fail,
    )
  },
  updateIpStatementStatus(id, toEnable, success, fail) {
    api.authenticatedRequest("POST", `/challenges/${id}/ipstatements`, { enabled: toEnable }, success, fail)
  },
  addIpStatement(id, data, success, fail) {
    api.authenticatedRequest("PUT", `/challenges/${id}/ipstatements/statements`, data, success, fail)
  },
  transferIpStatement(id, statementId, data, success, fail) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${id}/ipstatements/statements/${statementId}/transfer`,
      data,
      success,
      fail,
    )
  },
  removeIpStatement(id, statementId, success, fail) {
    api.authenticatedRequest("DELETE", `/challenges/${id}/ipstatements/statements/${statementId}`, null, success, fail)
  },
  setActiveIpStatement(id, statementId, success, fail) {
    api.authenticatedRequest("PUT", `/challenges/${id}/ipstatements/statements/${statementId}`, {}, success, fail)
  },
  updateIpStatement(id, statementId, data, success, fail) {
    api.authenticatedRequest("POST", `/challenges/${id}/ipstatements/statements/${statementId}`, data, success, fail)
  },
  activity(id, params, success, fail) {
    const queryParams = new SearchParams(params).toString()
    api.authenticatedRequest("GET", `/challenges/${id}/activity?${queryParams}`, null, success, fail)
  },
  createFromIdea(data, success, fail) {
    api.authenticatedRequest("POST", "/challenges/createFromIdea", data, success, fail)
  },
  getAssessmentDashboard(
    id,
    options: OpenAPI.GET<"/challenges/{id}/assessments/dashboard">["parameters"]["query"],
    success: (data: OpenAPI.GET<"/challenges/{id}/assessments/dashboard">["response"]) => void,
    fail,
  ) {
    const params = new SearchParams(options).toSafeString()
    api.authenticatedRequest("GET", `/challenges/${id}/assessments/dashboard${params}`, null, success, fail)
  },
  getAssessor(
    id,
    assessorId,
    success: (data: OpenAPI.GET<"/challenges/{id}/assessments/dashboard/{assessor_id}">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("GET", `/challenges/${id}/assessments/dashboard/${assessorId}`, null, success, fail)
  },
  getPotentialAssessorUsers(
    id: string,
    options: OpenAPI.GET<"/challenges/{id}/assessors/potential/users">["parameters"]["query"],
    success: (data: OpenAPI.GET<"/challenges/{id}/assessors/potential/users">["response"]) => void,
    fail,
  ) {
    const params = new SearchParams(options).toSafeString()
    api.authenticatedRequest("GET", `/challenges/${id}/assessors/potential/users${params}`, null, success, fail)
  },
  getPotentialAssessorEmails(
    id: string,
    options: OpenAPI.GET<"/challenges/{id}/assessors/potential/emails">["parameters"]["query"],
    success: (data: OpenAPI.GET<"/challenges/{id}/assessors/potential/emails">["response"]) => void,
    fail,
  ) {
    const params = new SearchParams(options).toSafeString()
    api.authenticatedRequest("GET", `/challenges/${id}/assessors/potential/emails${params}`, null, success, fail)
  },
  addPotentialAssessors(id: string, users: string[], emails: string[], success, fail) {
    api.authenticatedRequest("POST", `/challenges/${id}/assessors/potential`, { users, emails }, success, fail)
  },
  removePotentialAssessors(id: string, users: string[], emails: string[], success, fail) {
    api.authenticatedRequest("DELETE", `/challenges/${id}/assessors/potential`, { users, emails }, success, fail)
  },
  genAIGenerateChallengeDetails(data: { details: string[] }, success, fail) {
    api.authenticatedRequest("POST", "/challenges/ai/details", data, success, fail)
  },
  genAIGenerateTags(data: { details: string[] }, success, fail) {
    api.authenticatedRequest("POST", "/challenges/ai/tags", data, success, fail)
  },
  genAIImagePrompt(data: { details: string[] }, success, fail) {
    api.authenticatedRequest("POST", "/challenges/ai/image_prompt", data, success, fail)
  },
  getCalendar(id: string, success, fail) {
    api.authenticatedRequest("GET", `/challenges/${id}/calendar`, null, success, fail)
  },
  updateCalendar(id: string, data, success, fail) {
    api.authenticatedRequest("POST", `/challenges/${id}/calendar`, data, success, fail)
  },
  shiftCalendarEvents(id: string, data, success, fail) {
    api.authenticatedRequest("POST", `/challenges/${id}/calendar/shift`, data, success, fail)
  },
  createCalendarEvent(id: string, event, success, fail) {
    api.authenticatedRequest("POST", `/challenges/${id}/calendar/events`, event, success, fail)
  },
  updateCalendarEvent(id: string, eventId: string, event, success, fail) {
    api.authenticatedRequest("POST", `/challenges/${id}/calendar/events/${eventId}`, event, success, fail)
  },
  deleteCalendarEvent(id: string, eventId: string, success, fail) {
    api.authenticatedRequest("DELETE", `/challenges/${id}/calendar/events/${eventId}`, null, success, fail)
  },
  leaveCalendarEvent(id: string, eventId: string, success, fail) {
    api.authenticatedRequest("POST", `/challenges/${id}/calendar/events/${eventId}/leave`, null, success, fail)
  },
  importCalendar(fromId: string, toId: string, data, success, fail) {
    api.authenticatedRequest("POST", `/challenges/${toId}/calendar/import/${fromId}`, data, success, fail)
  },
  getCalendarSyncUrl(
    id: string,
    success: (data: OpenAPI.GET<"/challenges/{challenge_id}/calendar/sync">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("GET", `/challenges/${id}/calendar/sync`, null, success, fail)
  },
  generatePlan(id: string, data, success, fail) {
    api.authenticatedRequest("POST", `/challenges/${id}/calendar/plans`, data, success, fail)
  },
  deletePlan(id: string, planId: string, success, fail) {
    api.authenticatedRequest("DELETE", `/challenges/${id}/calendar/plans/${planId}`, null, success, fail)
  },
  getQuestions(
    id: string,
    success: (data: OpenAPI.GET<"/challenges/{challenge_id}/questions">["response"]) => void,
    fail,
  ) {
    api.maybeAuthenticatedRequest("GET", `/challenges/${id}/questions`, null, success, fail)
  },
  createQuestion(
    id: string,
    data: OpenAPI.POST<"/challenges/{challenge_id}/questions">["requestBody"],
    success: (data: OpenAPI.POST<"/challenges/{challenge_id}/questions">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("POST", `/challenges/${id}/questions`, data, success, fail)
  },
  updateQuestion(
    id: string,
    questionId: string,
    data: OpenAPI.POST<"/challenges/{challenge_id}/questions/{question_id}">["requestBody"],
    success: (data: OpenAPI.POST<"/challenges/{challenge_id}/questions/{question_id}">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("POST", `/challenges/${id}/questions/${questionId}`, data, success, fail)
  },
  deleteQuestion(
    id: string,
    questionId: string,
    success: (data: OpenAPI.DELETE<"/challenges/{challenge_id}/questions/{question_id}">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("DELETE", `/challenges/${id}/questions/${questionId}`, null, success, fail)
  },
  publishQuestion(
    id: string,
    questionId: string,
    success: (data: OpenAPI.POST<"/challenges/{challenge_id}/questions/{question_id}/publish">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("POST", `/challenges/${id}/questions/${questionId}/publish`, null, success, fail)
  },
  unpublishQuestion(
    id: string,
    questionId: string,
    success: (data: OpenAPI.POST<"/challenges/{challenge_id}/questions/{question_id}/unpublish">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("POST", `/challenges/${id}/questions/${questionId}/unpublish`, null, success, fail)
  },
  reorderQuestion(
    id: string,
    questionId: string,
    order: number,
    success: (data: OpenAPI.POST<"/challenges/{challenge_id}/questions/{question_id}/order">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("POST", `/challenges/${id}/questions/${questionId}/order`, { order }, success, fail)
  },
  announceQuestions(
    id: string,
    data: { questionIds: string[] },
    success: (data: OpenAPI.POST<"/challenges/{challenge_id}/questions/announce">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("POST", `/challenges/${id}/questions/announce`, data, success, fail)
  },
  copyQuestions(id: string, fromChallengeId: string, success: () => void, fail) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${id}/questions/copy?fromChallengeId=${fromChallengeId}`,
      null,
      success,
      fail,
    )
  },
  copy(id: string, success, fail) {
    api.authenticatedRequest("POST", `/challenges/${id}/copy`, null, success, fail)
  },
  copyImpacts(id: string, fromChallengeId: string, success, fail) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${id}/copy/impacts?fromChallengeId=${fromChallengeId}`,
      null,
      success,
      fail,
    )
  },
  copyFields: (
    challengeId: string,
    data: OpenAPI.POST<"/challenges/{challenge_id}/fields/copy">["requestBody"],
    success: (data: OpenAPI.POST<"/challenges/{challenge_id}/fields/copy">["response"]) => void,
    fail,
  ) => {
    api.authenticatedRequest("POST", `/challenges/${challengeId}/fields/copy`, data, success, fail)
  },
  reel: (success, fail) => {
    return api.authenticatedRequest("GET", "/challenges/reel", null, success, fail)
  },
})
